<script lang="ts">
import type { UploadScope as UploadScopeType } from '#layers/assets/stores/upload';

export interface IconImagePickerTabImageProps {
	scope: UploadScopeType;
	readonly?: boolean;
	disabled?: boolean;
}
</script>

<script lang="ts" setup>
import type { GlobalFileId } from '#layers/data-model/public';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RenderAssetImage from '#layers/assets/components/RenderAssetImage.vue';
import { useFileUpload } from '#layers/assets/composables/use-file-upload';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';
import { UploadScope } from '#layers/assets/stores/upload';
import { useToastResultHandling } from '#layers/ui/public';

const { scope, readonly, disabled } = defineProps<IconImagePickerTabImageProps>();

const emit = defineEmits<{
	uploadStart: [];
	select: [fileId: GlobalFileId];
}>();

const image = defineModel<GlobalFileId | null>();

const { t } = useI18n();
const { resolveUploadErrorMessage } = useFileUploadLimit();
const { onError: showErrorToast } = useToastResultHandling({
	error: { title: t('upload.failed_to_upload_file') },
});
const { uploadFiles, isUploading } = useFileUpload({
	scope,
	onError: (error) => showErrorToast(error, { description: resolveUploadErrorMessage(error) }),
});

const selectedFile = ref<File | null>(null);

async function handleSelectedFile(file: File | null) {
	if (!file || readonly || disabled || isUploading.value) {
		selectedFile.value = null;
		return;
	}

	selectedFile.value = file;
	emit('uploadStart');

	const [uploaded] = await uploadFiles([file]);
	selectedFile.value = null;

	if (uploaded) emit('select', uploaded.id);
}
</script>

<template>
	<div class="flex min-h-0 flex-1 flex-col gap-3 p-4">
		<div
			v-if="image"
			data-testid="icon-picker-image-preview"
			class="flex flex-col items-center gap-3 rounded-md border border-default p-4"
		>
			<RenderAssetImage
				:file="image"
				:workspace="scope === UploadScope.Workspace"
				class="size-20 rounded-md object-cover"
			/>
		</div>

		<FileUpload
			:model-value="selectedFile"
			data-testid="icon-picker-image-upload"
			accept="image/*"
			:disabled="readonly || disabled || isUploading"
			:aria-label="$t('icon_picker.upload_image')"
			@update:model-value="handleSelectedFile"
		/>

		<div v-if="isUploading" class="flex items-center justify-center gap-2 text-sm text-muted">
			<DSpinner />
			<span role="status">{{ $t('upload.status.uploading') }}</span>
		</div>
	</div>
</template>
